import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Trash2, Plus, FileDown } from 'lucide-react';
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API_BASE_URL } from "../config/apiconfig";

const EditProfile = () => {
    const [resumeFile, setResumeFile] = useState(null);
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState({
            firstName: "",
            lastName: "",
            location: "",
            phoneCode: "+94",
            phoneNumber: "",
            email: "",
            nic: "",
            summary: "Add a personal summary to introduce yourself.",
            open_to_work: 0,
            careers: [{
                companyName: "",
                jobPosition: "",
                startDate: "",
                endDate: ""
            }],
            education: [{
                instituteName: "",
                degreeProgram: "",
                startDate: "",
                endDate: ""
            }],
            licenses: [{
                name: "",
                issuer: "",
                date: ""
            }],
            skills: [],
            languages: []
        });
    
        const handleFileUpload = (event, type) => {
            const file = event.target.files[0];
            if (type === 'resume') {
                if (file && (file.type === 'application/pdf' || file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')) {
                    setResumeFile(file);
                } else {
                    alert('Please upload a valid resume file (PDF or Word)');
                }
            }
        };
    
    const [tempData, setTempData] = useState({});
    
    const handleFieldChange = (field, value) => {
        setTempData((prev) => ({ ...prev, [field]: value }));
    };

        const [isEditOpen, setIsEditOpen] = useState(false);
        const [isPdfReady, setIsPdfReady] = useState(false);

    // Add new item handlers
    const addCareer = () => {
        setTempData(prev => ({
            ...prev,
            careers: [...(prev.careers || profileData.careers), {
                companyName: "",
                jobPosition: "",
                startDate: "",
                endDate: ""
            }]
        }));
    };

    const addEducation = () => {
        setTempData(prev => ({
            ...prev,
            education: [...(prev.education || profileData.education), {
                instituteName: "",
                degreeProgram: "",
                startDate: "",
                endDate: ""
            }]
        }));
    };

    const addLicense = () => {
        setTempData(prev => ({
            ...prev,
            licenses: [...(prev.licenses || profileData.licenses), {
                name: "",
                issuer: "",
                date: ""
            }]
        }));
    };

    // Remove item handlers
    const removeItem = (array, index, field) => {
        setTempData(prev => ({
            ...prev,
            [field]: array.filter((_, i) => i !== index)
        }));
    };

    // Handle array field changes
    const handleArrayFieldChange = (index, field, subField, value, arrayName) => {
        setTempData(prev => {
            const array = [...(prev[arrayName] || profileData[arrayName])];
            array[index] = {
                ...array[index],
                [subField]: value
            };
            return {
                ...prev,
                [arrayName]: array
            };
        });
    };

     useEffect(() => {
            const fetchProfileData = async () => {
                try {
                    setLoading(true);
                    setError(null);
                    
                    if (!user?.email) {
                        setLoading(false);
                        return;
                    }
        
                    const response = await fetch(`${API_BASE_URL}/profile/${user.email}`);
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    
                    const data = await response.json();
                    setProfileData(prevData => ({
                        ...prevData,
                        ...data,
                        nic: data.nic || prevData.nic || "",
                        careers: data.careers || prevData.careers,
                        education: data.education || prevData.education,
                        licenses: data.licenses || prevData.licenses,
                        skills: data.skills || prevData.skills,
                        languages: data.languages || prevData.languages,
                        open_to_work: typeof data.open_to_work === 'number' || typeof data.open_to_work === 'boolean' ? (data.open_to_work ? 1 : 0) : (prevData.open_to_work || 0)
                    }));
                    setIsPdfReady(true);
                } catch (err) {
                    console.error('Error fetching profile:', err);
                    setError(err.message);
                } finally {
                    setLoading(false);
                }
            };
        
            fetchProfileData();
        }, [user]);

        const handleResumeDownload = async () => {
            try {
                if (!user?.email) {
                    throw new Error('Please log in to download resume');
                }
        
                // Show loading state
                const response = await fetch(`${API_BASE_URL}/profile/${user.email}/resume`);
                
                // Check for specific error cases
                if (response.status === 404) {
                    throw new Error('No resume found for this profile');
                }
                
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Failed to download resume');
                }
        
                // Get the filename from the Content-Disposition header
                const contentDisposition = response.headers.get('Content-Disposition');
                let filename = 'resume';
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }
        
                // Create blob and download
                const blob = await response.blob();
                if (blob.size === 0) {
                    throw new Error('Downloaded file is empty');
                }
        
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } catch (error) {
                console.error('Error downloading resume:', error);
                // Show error for longer duration (5 seconds)
                alert(`Resume download failed: ${error.message}`);
            }
        };
    
        const saveAllChanges = async () => {
            try {
                if (!user?.email) {
                    throw new Error('User email not available');
                }
        
                const formData = new FormData();
                
                // Create a properly structured profile data object
                const dataToSend = {
                    ...profileData,
                    ...tempData,
                    // Format skills and languages correctly for the backend
                    skills: Array.isArray(tempData.skills) 
                        ? tempData.skills 
                        : (typeof tempData.skills === 'string' 
                            ? tempData.skills?.split(',').map(skill => skill.trim()) 
                            : profileData.skills),
                    languages: Array.isArray(tempData.languages)
                        ? tempData.languages
                        : (typeof tempData.languages === 'string' 
                            ? tempData.languages?.split(',').map(lang => lang.trim()) 
                            : profileData.languages)
                };
        
                // Add profile data as a JSON string
                formData.append('profileData', JSON.stringify(dataToSend));
                
                // Add resume file if available
                if (resumeFile) {
                    formData.append('resume', resumeFile);
                }
                
                const response = await fetch(`${API_BASE_URL}/profile/${user.email}`, {
                    method: "PUT",
                    body: formData,
                    // Don't set Content-Type header when using FormData
                });
                
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || 'Error saving profile changes');
                }
                
                // Backend returns a minimal payload (message + counts), not the full profile.
                // Merge the data we sent into local state so UI reflects changes immediately.
                await response.json();
                setProfileData(prev => ({ ...prev, ...dataToSend }));
                setTempData({});
                setIsEditOpen(false);
                alert('Profile updated successfully');
            } catch (error) {
                console.error('Error saving changes:', error);
                alert(error.message || 'Error saving changes. Please try again.');
            }
        };
    
    const renderEducationFields = () => (
            <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="text-xl font-semibold text-gray-800">Education</h3>
                    <button 
                        className="text-indigo-600 hover:text-indigo-800"
                        onClick={addEducation}
                    >
                        <Plus size={20} />
                    </button>
                </div>
                {(tempData.education || profileData.education).map((education, index) => (
                    <div key={index} className="mb-4 p-4 border rounded-lg relative">
                        <button 
                            className="absolute top-2 right-2 text-red-600 hover:text-red-800"
                            onClick={() => removeItem(tempData.education || profileData.education, index, 'education')}
                        >
                            <Trash2 size={16} />
                        </button>
                        <div className="grid grid-cols-1 gap-4">
                            <input
                                type="text"
                                placeholder="Institute Name"
                                className="w-full p-2 border rounded"
                                value={education.instituteName}
                                onChange={(e) => handleArrayFieldChange(index, 'education', 'instituteName', e.target.value, 'education')}
                            />
                            <input
                                type="text"
                                placeholder="Degree Program"
                                className="w-full p-2 border rounded"
                                value={education.degreeProgram}
                                onChange={(e) => handleArrayFieldChange(index, 'education', 'degreeProgram', e.target.value, 'education')}
                            />
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm text-gray-600">Start Date</label>
                                    <input
                                        type="date"
                                        className="w-full p-2 border rounded mt-1"
                                        value={education.startDate}
                                        onChange={(e) => handleArrayFieldChange(index, 'education', 'startDate', e.target.value, 'education')}
                                    />
                                </div>
                                <div>
                                    <label className="text-sm text-gray-600">End Date</label>
                                    <input
                                        type="date"
                                        className="w-full p-2 border rounded mt-1"
                                        value={education.endDate}
                                        onChange={(e) => handleArrayFieldChange(index, 'education', 'endDate', e.target.value, 'education')}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
        
        const renderLicenseFields = () => (
            <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="text-xl font-semibold text-gray-800">Licenses & Certifications</h3>
                    <button 
                        className="text-indigo-600 hover:text-indigo-800"
                        onClick={addLicense}
                    >
                        <Plus size={20} />
                    </button>
                </div>
                {(tempData.licenses || profileData.licenses).map((license, index) => (
                    <div key={index} className="mb-4 p-4 border rounded-lg relative">
                        <button 
                            className="absolute top-2 right-2 text-red-600 hover:text-red-800"
                            onClick={() => removeItem(tempData.licenses || profileData.licenses, index, 'licenses')}
                        >
                            <Trash2 size={16} />
                        </button>
                        <div className="grid grid-cols-1 gap-4">
                            <input
                                type="text"
                                placeholder="License/Certificate Name"
                                className="w-full p-2 border rounded"
                                value={license.name}
                                onChange={(e) => handleArrayFieldChange(index, 'licenses', 'name', e.target.value, 'licenses')}
                            />
                            <input
                                type="text"
                                placeholder="Issuing Organization"
                                className="w-full p-2 border rounded"
                                value={license.issuer}
                                onChange={(e) => handleArrayFieldChange(index, 'licenses', 'issuer', e.target.value, 'licenses')}
                            />
                            <div>
                                <label className="text-sm text-gray-600">Issue Date</label>
                                <input
                                    type="date"
                                    className="w-full p-2 border rounded mt-1"
                                    value={license.date}
                                    onChange={(e) => handleArrayFieldChange(index, 'licenses', 'date', e.target.value, 'licenses')}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    
        const renderCareerFields = () => (
            <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="text-xl font-semibold text-gray-800">Career History</h3>
                    <button 
                        className="text-indigo-600 hover:text-indigo-800"
                        onClick={addCareer}
                    >
                        <Plus size={20} />
                    </button>
                </div>
                {(tempData.careers || profileData.careers).map((career, index) => (
                    <div key={index} className="mb-4 p-4 border rounded-lg relative">
                        <button 
                            className="absolute top-2 right-2 text-red-600 hover:text-red-800"
                            onClick={() => removeItem(tempData.careers || profileData.careers, index, 'careers')}
                        >
                            <Trash2 size={16} />
                        </button>
                        <div className="grid grid-cols-1 gap-4">
                            <input
                                type="text"
                                placeholder="Company Name"
                                className="w-full p-2 border rounded"
                                value={career.companyName}
                                onChange={(e) => handleArrayFieldChange(index, 'careers', 'companyName', e.target.value, 'careers')}
                            />
                            <input
                                type="text"
                                placeholder="Position"
                                className="w-full p-2 border rounded"
                                value={career.jobPosition}
                                onChange={(e) => handleArrayFieldChange(index, 'careers', 'jobPosition', e.target.value, 'careers')}
                            />
                            <div className="grid grid-cols-2 gap-4">
                                <input
                                    type="date"
                                    className="w-full p-2 border rounded"
                                    value={career.startDate}
                                    onChange={(e) => handleArrayFieldChange(index, 'careers', 'startDate', e.target.value, 'careers')}
                                />
                                <input
                                    type="date"
                                    className="w-full p-2 border rounded"
                                    value={career.endDate}
                                    onChange={(e) => handleArrayFieldChange(index, 'careers', 'endDate', e.target.value, 'careers')}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    
    return (
        <div className="edit-profile-page min-h-screen bg-gray-100">
            <Header />
            <div className="container mx-auto px-4 py-8">
                <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-lg p-8">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-semibold text-gray-800">Edit Profile</h2>
                        <button 
                            className="px-4 py-2 bg-gray-200 rounded-md"
                            onClick={() => navigate('/profile')}>
                            Cancel
                        </button>
                    </div>
                    
                    {/* Basic Info */}
                    <div className="profile-basic-info mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">Basic Info</h3>
                        <div className="mb-4">
                            <label className="block text-lg font-semibold text-gray-600">First Name:</label>
                            <input 
                                type="text" 
                                className="w-full p-3 border border-gray-300 rounded-md text-lg" 
                                value={tempData.firstName || profileData.firstName} 
                                onChange={(e) => handleFieldChange("firstName", e.target.value)} />
                        </div>
                        <div className="mb-4">
                            <label className="block text-lg font-semibold text-gray-600">Last Name:</label>
                            <input 
                                type="text" 
                                className="w-full p-3 border border-gray-300 rounded-md text-lg" 
                                value={tempData.lastName || profileData.lastName} 
                                onChange={(e) => handleFieldChange("lastName", e.target.value)} />
                        </div>
                        <div className="mb-4">
                            <label className="block text-lg font-semibold text-gray-600">NIC Number (optional):</label>
                            <input 
                                type="text" 
                                className="w-full p-3 border border-gray-300 rounded-md text-lg" 
                                value={tempData.nic ?? profileData.nic} 
                                onChange={(e) => handleFieldChange("nic", e.target.value)} />
                        </div>
                        <div className="mb-4">
                            <label className="block text-lg font-semibold text-gray-600">Location:</label>
                            <input 
                                type="text" 
                                className="w-full p-3 border border-gray-300 rounded-md text-lg" 
                                value={tempData.location || profileData.location} 
                                onChange={(e) => handleFieldChange("location", e.target.value)} />
                        </div>
                        <div className="mb-4">
                            <label className="block text-lg font-semibold text-gray-600">Phone Number:</label>
                            <input 
                                type="text" 
                                className="w-full p-3 border border-gray-300 rounded-md text-lg" 
                                value={tempData.phoneNumber || profileData.phoneNumber} 
                                onChange={(e) => handleFieldChange("phoneNumber", e.target.value)} />
                        </div>
                    </div>
                    
                    {/* Resume upload and download */}
                    <div className="flex items-center space-x-4 mb-6">
                        <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={(e) => handleFileUpload(e, 'resume')}
                            className="hidden"
                            id="resume-upload"
                        />
                        <label
                            htmlFor="resume-upload"
                            className="cursor-pointer flex items-center space-x-2 bg-white border border-gray-300 rounded-md px-4 py-2 hover:bg-gray-50"
                        >
                            <span>{resumeFile ? resumeFile.name : 'Upload Resume'}</span>
                        </label>
                        <button
                            onClick={handleResumeDownload}
                            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                        >
                            <FileDown size={20} />
                            <span>Download Resume</span>
                        </button>
                    </div>

                    {/* Open to Work toggle */}
                    <div className="mb-6">
                        <label className="inline-flex items-center cursor-pointer select-none">
                            <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={(tempData.open_to_work ?? profileData.open_to_work) ? true : false}
                                onChange={(e) => handleFieldChange('open_to_work', e.target.checked ? 1 : 0)}
                            />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-green-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:h-5 after:w-5 after:bg-white after:border after:border-gray-300 after:rounded-full after:transition-all after:ml-0.5 after:mt-0.5 peer-checked:bg-green-500"></div>
                            <span className="ml-3 text-gray-700 font-medium">Open to Work</span>
                        </label>
                        <p className="text-sm text-gray-500 mt-1">Enable to show an "Open to Work" ring on your profile picture.</p>
                    </div>

                    {/* Career, Education, and License sections */}
                    {renderCareerFields()}
                    {renderEducationFields()}
                    {renderLicenseFields()}
                    
                    {/* Skills and Languages as tag inputs */}
                    <div className="mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">Skills</h3>
                        <input 
                            type="text" 
                            className="w-full p-3 border rounded-md"
                            placeholder="Enter skills separated by commas"
                            value={tempData.skills || profileData.skills.join(', ')}
                            onChange={(e) => handleFieldChange("skills", e.target.value)}
                        />
                    </div>

                    <div className="mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">Languages</h3>
                        <input 
                            type="text" 
                            className="w-full p-3 border rounded-md"
                            placeholder="Enter languages separated by commas"
                            value={tempData.languages || profileData.languages.join(', ')}
                            onChange={(e) => handleFieldChange("languages", e.target.value)}
                        />
                    </div>

                    {/* Summary */}
                    <div className="profile-summary mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">Summary</h3>
                        <div className="mb-4">
                            <textarea 
                                className="w-full p-3 border border-gray-300 rounded-md text-lg" 
                                rows="4"
                                value={tempData.summary || profileData.summary} 
                                onChange={(e) => handleFieldChange("summary", e.target.value)} />
                        </div>
                    </div>
                    
                    <div className="flex justify-end mt-6">
                        <button 
                            className="save-changes-btn bg-indigo-600 text-white px-6 py-2 rounded-md text-lg hover:bg-indigo-700"
                            onClick={saveAllChanges}>
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default EditProfile;